import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import { toast } from 'sonner';
import { Check, X, Zap, Crown, Shield, ArrowRight, Loader2, CreditCard } from 'lucide-react';

const PLAN_STYLE = {
  starter: { icon: Zap, gradient: 'from-slate-50 to-slate-100', border: 'border-slate-200', accent: '#64748B' },
  professional: { icon: Crown, gradient: 'from-[#FFF5F0] to-[#FEF0E7]', border: 'border-[#D44E20]/20', accent: '#D44E20' },
  enterprise: { icon: Shield, gradient: 'from-slate-900 to-slate-800', border: 'border-slate-700', accent: '#F59E0B', dark: true },
};

const FEATURE_LABELS = {
  dashboard: 'Dashboard & Analytics', analytics: 'Analytics avanzate', loyalty: 'Loyalty / Referral',
  engagement: 'Engagement & Gamification', comunicazione: 'Broadcast messaggi', comunicazione_pro: 'Comunicazione PRO (AI, Template, Bot)',
  shop: 'Shop & Ecommerce', settings: 'Impostazioni',
};

const LIMIT_LABELS = {
  max_customers: 'Clienti attivi', max_messages_month: 'Messaggi/mese',
  max_automations: 'Automazioni', max_rewards: 'Premi',
  max_venues: 'Punti vendita', max_staff: 'Staff',
};

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [currentPkgId, setCurrentPkgId] = useState(null);
  const [currentSlug, setCurrentSlug] = useState(null);
  const [subStatus, setSubStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);

  useEffect(() => {
    adminApi.get('/admin/plans').then(r => {
      setPlans(r.data.packages || []);
      setAddons(r.data.addons || []);
      setCurrentPkgId(r.data.current_package_id);
      setCurrentSlug(r.data.current_package_slug);
      setSubStatus(r.data.subscription_status);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const subscribe = async (pkgId, method = 'stripe') => {
    setSubscribing(pkgId);
    try {
      if (method === 'paypal') {
        const r = await adminApi.post('/admin/subscribe/paypal', {
          package_id: pkgId, origin_url: window.location.origin,
        });
        if (r.data.paypal_approval_url) {
          window.location.href = r.data.paypal_approval_url;
        }
      } else {
        const r = await adminApi.post('/admin/subscribe', {
          package_id: pkgId, origin_url: window.location.origin,
        });
        if (r.data.stripe_url) {
          window.location.href = r.data.stripe_url;
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
    setSubscribing(null);
  };

  const subscribeAddon = async (addonSlug) => {
    try {
      const r = await adminApi.post('/admin/subscribe/addon', {
        addon_slug: addonSlug,
        origin_url: window.location.origin,
      });
      if (r.data.stripe_url) {
        window.location.href = r.data.stripe_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  if (loading) return (
    <div className="p-8 flex items-center justify-center h-64">
      <div className="w-8 h-8 border-3 border-[#D44E20] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="admin-plans-page">
      <div className="text-center mb-10">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#1A1A1A]">Scegli il tuo piano</h1>
        <p className="text-[#64748B] mt-2">Sblocca le funzionalita che servono al tuo locale</p>
      </div>

      {/* Plans grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
        {plans.map(pkg => {
          const style = PLAN_STYLE[pkg.slug] || PLAN_STYLE.starter;
          const Icon = style.icon;
          const isCurrent = currentPkgId === pkg.id;
          const isDark = style.dark;
          const tc = isDark ? '#fff' : '#1A1A1A';
          const ts = isDark ? 'rgba(255,255,255,0.6)' : '#64748B';

          return (
            <div key={pkg.id}
              className={`relative rounded-2xl border ${style.border} bg-gradient-to-b ${style.gradient} p-6 flex flex-col ${
                pkg.slug === 'professional' ? 'ring-2 ring-[#D44E20] shadow-lg scale-[1.02]' : ''
              }`}
              data-testid={`plan-${pkg.slug}`}>

              {pkg.slug === 'professional' && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#D44E20] text-white text-[10px] font-bold rounded-full uppercase tracking-wider">
                  Piu popolare
                </div>
              )}

              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${style.accent}18` }}>
                  <Icon size={20} style={{ color: style.accent }} />
                </div>
                <div>
                  <h3 className="font-bold text-lg" style={{ color: tc }}>{pkg.name}</h3>
                </div>
              </div>

              <div className="mb-5">
                <span className="text-4xl font-bold" style={{ color: tc }}>{pkg.price_monthly}</span>
                <span className="text-sm ml-1" style={{ color: ts }}>EUR/mese</span>
              </div>

              <p className="text-xs mb-5 leading-relaxed" style={{ color: ts }}>{pkg.description}</p>

              {/* Features */}
              <div className="space-y-2 mb-6 flex-1">
                {Object.entries(FEATURE_LABELS).map(([key, label]) => {
                  const enabled = pkg.features?.[key];
                  return (
                    <div key={key} className="flex items-center gap-2">
                      {enabled ? (
                        <Check size={14} className="flex-shrink-0" style={{ color: style.accent }} />
                      ) : (
                        <X size={14} className="flex-shrink-0 opacity-30" style={{ color: ts }} />
                      )}
                      <span className={`text-xs ${enabled ? 'font-medium' : 'opacity-40'}`} style={{ color: enabled ? tc : ts }}>{label}</span>
                    </div>
                  );
                })}
              </div>

              {/* Limits */}
              <div className="mb-5 space-y-1 py-3" style={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}` }}>
                {Object.entries(pkg.limits || {}).map(([key, val]) => (
                  <div key={key} className="flex justify-between text-[11px]">
                    <span style={{ color: ts }}>{LIMIT_LABELS[key] || key}</span>
                    <span className="font-semibold" style={{ color: tc }}>{val === -1 ? 'Illimitati' : val.toLocaleString('it-IT')}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              {isCurrent && subStatus === 'active' ? (
                <div className="w-full py-3 rounded-xl text-center text-sm font-semibold" style={{ backgroundColor: `${style.accent}15`, color: style.accent }}>
                  Piano attuale
                </div>
              ) : (
                <div className="space-y-2">
                  <button onClick={() => subscribe(pkg.id, 'stripe')} disabled={subscribing === pkg.id}
                    data-testid={`subscribe-${pkg.slug}`}
                    className="w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all hover:shadow-lg active:scale-[0.98] disabled:opacity-50"
                    style={{
                      backgroundColor: pkg.slug === 'professional' ? '#D44E20' : isDark ? '#F59E0B' : style.accent,
                      color: '#fff',
                    }}>
                    {subscribing === pkg.id ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <>
                        <CreditCard size={14} />
                        {isCurrent ? 'Rinnova' : currentSlug ? 'Cambia piano' : 'Paga con carta'}
                      </>
                    )}
                  </button>
                  <button onClick={() => subscribe(pkg.id, 'paypal')} disabled={subscribing === pkg.id}
                    data-testid={`subscribe-paypal-${pkg.slug}`}
                    className="w-full py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all hover:shadow-md active:scale-[0.98] disabled:opacity-50"
                    style={{ backgroundColor: '#0070BA', color: '#fff' }}>
                    PayPal
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add-ons */}
      {addons.length > 0 && (
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl font-bold text-[#1A1A1A] mb-4 text-center">Add-on</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {addons.map(addon => (
              <div key={addon.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between" data-testid={`addon-${addon.slug}`}>
                <div>
                  <span className="text-sm font-semibold text-[#1A1A1A]">{addon.name}</span>
                  <p className="text-[10px] text-slate-400">{addon.unit_label}</p>
                  <p className="text-[10px] text-slate-400">Disponibile per: {addon.available_for?.join(', ')}</p>
                </div>
                <div className="text-right">
                  <span className="text-lg font-bold text-[#D44E20]">{addon.price}</span>
                  <span className="text-[10px] text-slate-400 ml-1">EUR</span>
                  {addon.price_type === 'monthly' && (
                    <button onClick={() => subscribeAddon(addon.slug)} className="block mt-1 text-[10px] text-[#D44E20] font-semibold hover:underline">
                      Attiva
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
